/**
 * The discussion beside a row.
 *
 * **Newest first, which is the endpoint's own order.** Upstream shows the
 * oldest at the top and loads older ones as you scroll up, which needs scroll
 * anchoring to keep the reader's place while the list grows above them. Newest
 * first needs none of that, and it puts what somebody has just said where they
 * are already looking.
 *
 * **Plain text, both ways.** A comment is stored as a ProseMirror document so
 * it can carry mentions; the endpoint accepts a plain string and builds the
 * document, and it renders the document back as `message_text` with mentions as
 * `@Name`. So this reads the rendering rather than the document -- rendering
 * the document here would be a second implementation of it, in another
 * language, kept in step by hand. Composing a mention is not built: it needs a
 * picker over the workspace's members, and typing `@Ada` is text, not a mention.
 */
import { useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { RowComment } from '@/api/types'
import { useSessionStore } from '@/features/auth/session-store'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { mergedPages } from '@/shared/lib/paged'
import { whenText } from '@/shared/lib/when'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { Skeleton } from '@/shared/ui/skeleton'
import { Textarea } from '@/shared/ui/textarea'
import { useCommentActions, useRowComments } from './comments'

export function CommentPanel({ tableId, rowId }: { tableId: number; rowId: number }) {
  const { t } = useTranslation()
  const comments = useRowComments(tableId, rowId)
  const actions = useCommentActions(tableId, rowId)
  const [draft, setDraft] = useState('')
  const held = mergedPages(comments.data)

  return (
    <section aria-label={t('comment.plural')} className="flex min-h-0 flex-col gap-3">
      <form
        onSubmit={(event) => {
          event.preventDefault()
          const message = draft.trim()
          // A comment made of spaces is one the backend refuses -- an empty
          // document does not validate -- so it is refused here rather than
          // sent to be turned down.
          if (message === '') return
          setDraft('')
          actions.post(message)
        }}
      >
        <Textarea
          value={draft}
          rows={2}
          aria-label={t('comment.write')}
          placeholder={t('comment.write')}
          onChange={(event) => {
            setDraft(event.target.value)
          }}
        />
        <div className="mt-2 flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={draft.trim() === '' || actions.isPosting}
          >
            {t('comment.post')}
          </Button>
        </div>
      </form>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {comments.isPending ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-2/3" />
          </div>
        ) : held.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">{t('comment.none')}</p>
        ) : (
          <ul className="space-y-3">
            {held.map((comment) => (
              <CommentRow
                key={comment.id}
                comment={comment}
                onEdit={(message) => {
                  actions.edit(comment.id, message)
                }}
                onRemove={() => {
                  actions.remove(comment.id)
                }}
              />
            ))}
          </ul>
        )}

        {comments.hasNextPage && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full"
            disabled={comments.isFetchingNextPage}
            onClick={() => {
              void comments.fetchNextPage()
            }}
          >
            {t('comment.more')}
          </Button>
        )}
      </div>
    </section>
  )
}

function CommentRow({
  comment,
  onEdit,
  onRemove,
}: {
  comment: RowComment
  onEdit: (message: string) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  // Read from the session rather than passed down: the panel is several
  // components from anything that knows who is signed in, and the store is
  // where that already lives.
  const userId = useSessionStore((state) => state.user?.id)
  const [editing, setEditing] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  // Only the author may change or remove a comment, which the backend enforces
  // and the interface should not offer past. A comment whose author has left
  // belongs to nobody: `user_id` is null, and null is not this user.
  const mine = comment.user_id !== null && comment.user_id === userId
  // Empty rather than absent when the author's account has been removed: the
  // comment outlives them, and a comment attributed to nobody at all reads as a
  // rendering fault rather than as a fact about the account.
  const author =
    comment.first_name === '' ? t('comment.removedAuthor') : comment.first_name

  return (
    <li className="rounded border p-2">
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{author}</span>{' '}
          {whenText(comment.created_on)}
          {comment.edited && <> · {t('comment.edited')}</>}
        </p>
        {mine && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-6 shrink-0"
                aria-label={t('comment.actions')}
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => {
                  setEditing(comment.message_text)
                }}
              >
                {t('comment.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => {
                  setConfirming(true)
                }}
              >
                {t('action.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {editing === null ? (
        <p className="mt-1 text-sm break-words whitespace-pre-wrap">
          {comment.message_text}
        </p>
      ) : (
        <form
          className="mt-1"
          onSubmit={(event) => {
            event.preventDefault()
            const message = editing.trim()
            if (message === '') return
            setEditing(null)
            // Sent even when it reads the same. The text is a rendering of the
            // document, so "unchanged text" is not "unchanged comment" -- a
            // mention renders as `@Ada` and would be sent back as those four
            // characters, which is a real edit and the one thing this build can
            // do to a comment it cannot compose.
            onEdit(message)
          }}
        >
          <Textarea
            value={editing}
            rows={2}
            autoFocus
            aria-label={t('comment.editing')}
            onChange={(event) => {
              setEditing(event.target.value)
            }}
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(null)
              }}
            >
              {t('action.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={editing.trim() === ''}>
              {t('comment.save')}
            </Button>
          </div>
        </form>
      )}

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('comment.deleteTitle')}
        description={t('comment.deleteDescription')}
        confirmLabel={t('action.delete')}
        destructive
        onConfirm={onRemove}
      />
    </li>
  )
}
